/**
 * VedettaConeModal — Modale per ruotare e regolare il cono di osservazione della vedetta.
 * Renderizzato via ReactDOM.createPortal fuori dalla mappa Leaflet per evitare
 * conflitti con gli eventi touch del mappa (pan/zoom).
 */
import React from 'react';
import ReactDOM from 'react-dom';
import type { Lookout } from '../../types';

interface VedettaConeModalProps {
  vedetta: Lookout;
  color: string;
  onUpdateAzimut:   (id: number, azimut: number) => void;
  onUpdateAmpiezza: (id: number, ampiezza: number) => void;
  onClose: () => void;
}

const DIRECTIONS = [
  { label: 'N',  deg: 0   },
  { label: 'NE', deg: 45  },
  { label: 'E',  deg: 90  },
  { label: 'SE', deg: 135 },
  { label: 'S',  deg: 180 },
  { label: 'SO', deg: 225 },
  { label: 'O',  deg: 270 },
  { label: 'NO', deg: 315 },
];

export const VedettaConeModal: React.FC<VedettaConeModalProps> = ({
  vedetta, color, onUpdateAzimut, onUpdateAmpiezza, onClose,
}) => {
  const azimut   = vedetta.settoreAzimut  ?? 0;
  const ampiezza = vedetta.settoreAmpiezza ?? 90;

  const modal = (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0f172a', borderRadius: '16px 16px 0 0',
          border: '1px solid rgba(255,255,255,0.12)',
          width: '100%', maxWidth: 480,
          padding: '20px 24px 36px',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.6)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#f1f5f9' }}>
              🔭 {vedetta.nominativo}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2 }}>
              Cono di osservazione
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#94a3b8', borderRadius: 8, padding: '6px 12px', fontSize: '1rem', cursor: 'pointer' }}
          >✕</button>
        </div>

        {/* Direzione azimut */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 600 }}>🧭 Direzione</span>
            <span style={{ color, fontWeight: 800, fontSize: '1.1rem' }}>{azimut}°</span>
          </div>
          <input
            type="range" min={0} max={359} value={azimut}
            style={{ width: '100%', accentColor: color, height: 6, cursor: 'pointer' }}
            onChange={(e) => onUpdateAzimut(vedetta.id, parseInt(e.target.value))}
          />
          {/* Tasti direzione rapida */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, gap: 4 }}>
            {DIRECTIONS.map((d) => (
              <button
                key={d.label}
                onClick={() => onUpdateAzimut(vedetta.id, d.deg)}
                style={{
                  flex: 1, padding: '8px 0', borderRadius: 6, fontSize: '0.75rem', fontWeight: 700,
                  border: `1.5px solid ${Math.abs(azimut - d.deg) < 23 ? color : 'rgba(255,255,255,0.15)'}`,
                  background: Math.abs(azimut - d.deg) < 23 ? `${color}33` : 'rgba(255,255,255,0.05)',
                  color: Math.abs(azimut - d.deg) < 23 ? color : '#64748b',
                  cursor: 'pointer',
                }}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Ampiezza cono */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 600 }}>↔ Ampiezza cono</span>
            <span style={{ color, fontWeight: 800, fontSize: '1.1rem' }}>{ampiezza}°</span>
          </div>
          <input
            type="range" min={30} max={180} step={10} value={ampiezza}
            style={{ width: '100%', accentColor: color, height: 6, cursor: 'pointer' }}
            onChange={(e) => onUpdateAmpiezza(vedetta.id, parseInt(e.target.value))}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: '0.7rem', color: '#475569' }}>30° (stretto)</span>
            <span style={{ fontSize: '0.7rem', color: '#475569' }}>180° (panoramico)</span>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};
